import { FC, useCallback, useContext, useMemo, useRef } from "react";

import { Box, SelectChangeEvent, Stack } from "@mui/material";
import {
  DiscountType,
  AvailablePaymentCurrency,
  FormType,
  getTipAmountFromPercentage,
  getDollarValue,
  formatCurrencyAmount,
} from "@simplyk/common";
import { Control, useWatch } from "react-hook-form";

import { FormPaymentSummary } from "../../../../../components/forms-design-system/FormPaymentSummary";
import { FormPaymentOrderItem } from "../../../../../components/forms-design-system/FormPaymentSummary/FormPaymentOrderItem";
import { FormPaymentOrderSection } from "../../../../../components/forms-design-system/FormPaymentSummary/FormPaymentOrderSection";
import { FormPaymentTipSection } from "../../../../../components/forms-design-system/FormPaymentSummary/FormPaymentTipSection";
import { FormPaymentTipSelect } from "../../../../../components/forms-design-system/FormPaymentSummary/FormPaymentTipSelect";
import { FormTextButton } from "../../../../../components/forms-design-system/FormTextButton";
import { FormTypography } from "../../../../../components/forms-design-system/FormTypography";
import { FormLearnMoreOnZeffyDialog } from "../../../../../components/PaymentProcessor/FormLearnMoreOnZeffyDialog";
import { useTicketsDescriptions } from "../../../../../components/PaymentProcessor/hooks/useTicketsDescriptions";
import { useZeffyDescription } from "../../../../../components/PaymentProcessor/hooks/useZeffyDescription";
import { getRenewalDate } from "../../../../../components/PaymentProcessor/PaymentProcessorTotalWithTip";
import { useTicketingKeyValues } from "../../../../../components/TicketingDrawers/useTicketingKeyValues";
import { AmplitudeEvents } from "../../../../../constants/amplitude";
import { useFrontendAuctionContext } from "../../../../../contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../../contexts/FrontendFormContext";
import { FrontendTicketingContext } from "../../../../../contexts/FrontendTicketingContext";
import { useLocaleContext } from "../../../../../contexts/LocaleContext";
import { getDiscountReduction } from "../../../../../helpers/order";
import { useAmplitude } from "../../../../../hooks/amplitude/useAmplitude";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { TicketingPaymentInput } from "../../../../../types/ticketing";
import { useIsGhostPreview } from "../../../../LiveFormEditor/LivePreview/useIsGhostPreview";
import { InfoIconWithTooltip } from "../../../../LiveFormEditor/Steps/components/InfoIconWithTooltip";
import { DonationPaymentInput, useFormV2Context } from "../../../context/FormV2Context";
import { FormV2Step2SectionWrapper } from "../FormV2Step2SectionWrapper";
import { PublicDonationCheckbox, usePublicDonationCheckboxRenderPosition } from "../PublicDonationCheckBox";

import { InHonour } from "./InHonour";
import { TicketingV2RenewalAndDiscountsSection } from "./TicketingV2RenewalAndDiscountsSection";

import { AuctionPaymentInfoBox } from "@/features/FormV2/Step2/sections/SummarySection/AuctionPaymentInfoBox";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormChip } from "@/forms-design-system/FormChip";
import { isRequired } from "@/helpers/validators";

const ASK_TO_CONFIRM_TIP_AMOUNT_THRESHOLD = 10000;

export const FormV2SummarySection: FC = () => {
  const { t } = useTranslate();
  const { form, type, formObject, isAuction } = useFormV2Context();
  const isSummaryGhostPreview = useIsGhostPreview("TicketingV2Step2SummarySection");
  const publicDonationCheckboxPosition = usePublicDonationCheckboxRenderPosition();
  const { logAmplitudeEvent } = useAmplitude();
  const freeTipInputRef = useRef<HTMLInputElement | null>(null);

  const { hasAlreadyBid } = useFrontendAuctionContext();
  const { isoLocale } = useLocaleContext();
  const {
    formType,
    stripeRecurrenceInterval,
    currency,
    displayedFormAmount,
    suggestedTips,
    selectedTip,
    handleChangeFreeTips,
    freeTips,
    organization,
    logAmplitudeTipsChanged,
    handleChangeTips,
    getTipChoice,
    tip,
    formData,
    category,
    isSuggestedTipsAmount,
    displayTipSelection,
  } = useContext(FrontendFormContext);

  const { validDiscount, membershipRenewalBuyerInfo } = useContext(FrontendTicketingContext);
  const { showZeffyDescription, openZeffyDescription, closeZeffyDescription } = useZeffyDescription();

  const extraDonation = useWatch({ control: form.control as Control<TicketingPaymentInput>, name: "extraDonation" });
  const giveAsOrganism = useWatch({ control: form.control as Control<TicketingPaymentInput>, name: "giveAsOrganism" });
  const hasGiftAidConfirmation = useWatch({
    control: form.control as Control<TicketingPaymentInput>,
    name: "hasGiftAidConfirmation",
  });
  const organizationCurrency = currency || AvailablePaymentCurrency.Usd;
  const discountFormattedReduction = useMemo(
    () => validDiscount && getDiscountReduction(validDiscount, isoLocale, organizationCurrency),
    [organizationCurrency, isoLocale, validDiscount]
  );
  const { discountPrice } = useTicketingKeyValues({ control: form.control as Control<TicketingPaymentInput> });
  const { ticketsDescriptions } = useTicketsDescriptions({ control: form.control as Control<TicketingPaymentInput> });

  const isMonthlyDonation = formType === FormType.DonationForm && stripeRecurrenceInterval === "month";
  const isYearlyDonation = formType === FormType.DonationForm && stripeRecurrenceInterval === "year";
  const summaryTitle = (() => {
    if (hasAlreadyBid) {
      return (
        <Stack direction="row" alignItems="center">
          <FormTypography variant="subtitle1" component="span" sx={{ marginRight: 0.5 }}>
            {t("ticketing", "activeBids")}
          </FormTypography>

          <InfoIconWithTooltip
            tooltip={t("ticketing", "activeBidsTooltip")}
            iconProps={{ size: "small", vibe: "text-form-intense" }}
          />
        </Stack>
      );
    }
    if (isAuction) {
      return t("ticketing", "totalBids");
    }
    if (formType === FormType.Ticketing) {
      return t("ticketing", "order");
    }

    if (isMonthlyDonation) {
      return t("donationForm", "monthlyDonationCancelAnytime");
    }
    if (isYearlyDonation) {
      return t("donationForm", "yearlyDonationCancelAnytime");
    }
    return <>{t("donationForm", "donation")}</>;
  })();

  const onChangeTip = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      logAmplitudeEvent(AmplitudeEvents.DonorFormTipChanged, {
        amount: getDollarValue(displayedFormAmount),
        form_id: formData.id,
        form_type: category,
        organization_country: organization.country,
        tip_choice: getTipChoice(event.target.value as string),
        tip_amount: getDollarValue(getTipAmountFromPercentage(displayedFormAmount, Number(event.target.value))),
      });
      setTimeout(() => {
        if (event.target.value === "free" && freeTipInputRef?.current) {
          freeTipInputRef.current.focus();
        }
      }, 100);
      handleChangeTips(event);
    },
    [
      logAmplitudeEvent,
      displayedFormAmount,
      formData.id,
      category,
      organization.country,
      getTipChoice,
      handleChangeTips,
    ]
  );

  const renewalDate = getRenewalDate(membershipRenewalBuyerInfo, stripeRecurrenceInterval, formType);

  const shouldAskToConfirmTipAmount = tip >= displayedFormAmount && tip >= ASK_TO_CONFIRM_TIP_AMOUNT_THRESHOLD;
  const swiftaidEligibleAmount = type === FormType.DonationForm ? displayedFormAmount : extraDonation || 0;

  return (
    <FormV2Step2SectionWrapper title={t("ticketing", "summary")} isGhostPreview={isSummaryGhostPreview}>
      <Stack spacing={1}>
        {publicDonationCheckboxPosition === "summary-section" && <PublicDonationCheckbox />}
        {type === FormType.DonationForm && formObject.allowInHonour && <InHonour />}

        {type === FormType.Ticketing && <TicketingV2RenewalAndDiscountsSection />}

        {isAuction && <AuctionPaymentInfoBox />}

        <FormLearnMoreOnZeffyDialog open={showZeffyDescription} onClose={closeZeffyDescription} />

        <FormPaymentSummary
          currency={organizationCurrency}
          renewalDate={renewalDate}
          total={displayedFormAmount + tip}
          slots={{
            order: (
              <FormPaymentOrderSection
                title={summaryTitle}
                subtotal={displayedFormAmount}
                currency={organizationCurrency}
                recurrence={isMonthlyDonation ? "monthly" : isYearlyDonation ? "yearly" : undefined}
              >
                {ticketsDescriptions?.map((tickets, index) => (
                  <FormPaymentOrderItem
                    key={index}
                    label={
                      <>
                        <FormTypography variant="body2" component="span" vibe="moderate" data-test="ticket-description">
                          {tickets.description}
                        </FormTypography>
                        {tickets.isNew && (
                          <Box marginLeft={1} component="span">
                            <FormChip vibe="neutral" variant="outlined" label={t("ticketing", "newBid")} />
                          </Box>
                        )}
                        {tickets.recurrenceExpirationInfo && (
                          <FormTypography variant="subtitle2" component="span" vibe="supershy" paddingLeft={1}>
                            {tickets.recurrenceExpirationInfo}
                          </FormTypography>
                        )}
                      </>
                    }
                    price={tickets.price}
                    currency={organizationCurrency}
                  />
                ))}
                {validDiscount && (
                  <FormPaymentOrderItem
                    label={
                      t("ticketing", "discountValue") + " " + validDiscount?.type === DiscountType.Percentage
                        ? discountFormattedReduction || ""
                        : ""
                    }
                    price={discountPrice}
                    currency={organizationCurrency}
                  />
                )}
                {extraDonation && (
                  <>
                    <FormPaymentOrderItem
                      label={t("ticketing", "extraDonation")}
                      price={extraDonation}
                      currency={organizationCurrency}
                    />
                  </>
                )}
                {hasGiftAidConfirmation && !giveAsOrganism && (
                  <FormTypography variant="body2" vibe="brand">
                    {t("ticketing", "extraDonationWithSwiftAid", {
                      amount: formatCurrencyAmount({
                        value: getDollarValue(0.25 * swiftaidEligibleAmount),
                        locale: isoLocale,
                        currency: organizationCurrency,
                      }),
                    })}
                  </FormTypography>
                )}
              </FormPaymentOrderSection>
            ),
            tip: displayTipSelection ? (
              <FormPaymentTipSection
                organizationName={organization.name}
                organizationCountry={organization.country}
                slots={{
                  learnMore: (
                    <FormTextButton onClick={openZeffyDescription}>{t("donationForm", "knowMore")}</FormTextButton>
                  ),
                  tipSelect: (
                    <FormPaymentTipSelect
                      value={selectedTip}
                      options={suggestedTips}
                      currency={organizationCurrency}
                      onChange={onChangeTip}
                      onChangeFreeTips={handleChangeFreeTips}
                      freeTips={freeTips}
                      onBlurTip={logAmplitudeTipsChanged}
                      freeTipInputRef={freeTipInputRef}
                      isAuction={isAuction}
                      isSuggestedTipsAmount={isSuggestedTipsAmount}
                    />
                  ),
                  tipAmountConfirmation: shouldAskToConfirmTipAmount ? (
                    <FormCheckbox
                      control={form.control as Control<TicketingPaymentInput | DonationPaymentInput>}
                      name="confirmTipAmount"
                      rules={[isRequired(shouldAskToConfirmTipAmount, t("ticketing", "confirmTipAmountRequired"))]}
                      size="small"
                      label={t("ticketing", "confirmTipAmount")}
                      error={!!form.formState.errors.confirmTipAmount}
                      helperText={form.formState.errors.confirmTipAmount?.message}
                      data-test="confirm-tip-amount-checkbox"
                    />
                  ) : undefined,
                }}
                helperText={
                  isAuction
                    ? selectedTip.isFree
                      ? t("ticketing", "auction.payment.tip.customHelper")
                      : t("ticketing", "auction.payment.tip.helper")
                    : undefined
                }
              />
            ) : undefined,
          }}
        />
      </Stack>
    </FormV2Step2SectionWrapper>
  );
};
